// Utility to convert a Gregorian Date to a Hijri date string
// Core logic adapted from _lib/hijri_date.js
import miqaatsData from './data/miqaats.json';

const KABISA_YEAR_REMAINDERS = [2, 5, 8, 10, 13, 16, 19, 21, 24, 27, 29];

const DAYS_IN_YEAR = [30, 59, 89, 118, 148, 177, 207, 236, 266, 295, 325];

const DAYS_IN_30_YEARS = [
   354,  708, 1063, 1417, 1771, 2126, 2480, 2834,  3189,  3543,
  3898, 4252, 4606, 4961, 5315, 5669, 6024, 6378,  6732,  7087,
  7441, 7796, 8150, 8504, 8859, 9213, 9567, 9922, 10276, 10631,
];

// Eastern Arabic-Indic digit map (U+0660–U+0669), matching _lib/arabic_numerals.js
const ARABIC_DIGITS = ['\u0660','\u0661','\u0662','\u0663','\u0664','\u0665','\u0666','\u0667','\u0668','\u0669'];

function toArabicNumerals(n) {
  return Math.floor(n).toString().split("").map(d => ARABIC_DIGITS[parseInt(d, 10)]).join("");
}

const MONTH_NAMES = [
  "Moharram",
  "Safar",
  "Rabi al-Awwal",
  "Rabi al-Aakhar",
  "Jumada al-Ula",
  "Jumada al-Ukhra",
  "Rajab",
  "Shabaan",
  "Ramadaan",
  "Shawwal",
  "Zilqadah",
  "Zilhaj",
];

function isJulian(date) {
  if (date.getFullYear() < 1582) return true;
  if (date.getFullYear() === 1582) {
    if (date.getMonth() < 9) return true;
    if (date.getMonth() === 9 && date.getDate() < 5) return true;
  }
  return false;
}

function gregorianToAJD(date) {
  let year = date.getFullYear();
  let month = date.getMonth() + 1;
  const day = date.getDate();
  if (month < 3) { year--; month += 12; }
  let b;
  if (isJulian(date)) {
    b = 0;
  } else {
    const a = Math.floor(year / 100);
    b = 2 - a + Math.floor(a / 4);
  }
  return Math.floor(365.25 * (year + 4716)) + Math.floor(30.6001 * (month + 1)) + day + b - 1524.5;
}

function fromAJD(ajd) {
  let i = 0;
  let left = Math.floor(ajd - 1948083.5);
  const y30 = Math.floor(left / 10631.0);
  left -= y30 * 10631;
  while (left > DAYS_IN_30_YEARS[i]) { i++; }
  const year = Math.round(y30 * 30.0 + i);
  if (i > 0) left -= DAYS_IN_30_YEARS[i - 1];
  i = 0;
  while (left > DAYS_IN_YEAR[i]) { i++; }
  const month = Math.round(i);
  const date = i > 0 ? Math.round(left - DAYS_IN_YEAR[i - 1]) : Math.round(left);
  return { year, month, date };
}

/**
 * Returns the Hijri date string for a given Gregorian Date object.
 * Format: "DD MonthName YYYY AH"
 * @param {Date} date - Gregorian date (defaults to today)
 * @returns {string}
 */
export function getHijriDate(date = new Date()) {
  const ajd = gregorianToAJD(date);
  const { year, month, date: day } = fromAJD(ajd);
  return `${toArabicNumerals(day)} ${MONTH_NAMES[month]} ${year}H`;
}

/**
 * Returns Hijri day and month name only — for compact display (e.g. calendar cells).
 * Format: "DD MonthName"
 * @param {Date} date
 * @returns {string}
 */
export function getHijriDayMonth(date = new Date()) {
  const ajd = gregorianToAJD(date);
  const { month, date: day } = fromAJD(ajd);
  return `${toArabicNumerals(day)} ${MONTH_NAMES[month]}`;
}

/**
 * Returns Hijri day and month as separate parts for independent styling.
 * @param {Date} date
 * @returns {{ day: string, month: string, year: number }}
 */
export function getHijriParts(date = new Date()) {
  const ajd = gregorianToAJD(date);
  const { year, month, date: day } = fromAJD(ajd);
  return { day: toArabicNumerals(day), month: MONTH_NAMES[month], year };
}

/**
 * Returns Hijri day and month with plain English numerals — for use in PDF renderers
 * that lack Arabic-Indic font support.
 * @param {Date} date
 * @returns {string}  e.g. "12 Ramadaan"
 */
export function getHijriDayMonthEn(date = new Date()) {
  const ajd = gregorianToAJD(date);
  const { month, date: day } = fromAJD(ajd);
  return `${day} ${MONTH_NAMES[month]}`;
}

/**
 * Returns the list of miqaats for a given Gregorian date, looked up by Hijri day/month.
 * @param {Date} date
 * @returns {Array<{ title: string, description: string|null, phase: string, priority: number, year: number|null }>}
 */
export function getMiqaatsForDate(date = new Date()) {
  const ajd = gregorianToAJD(date);
  const { month, date: day } = fromAJD(ajd);
  const entry = miqaatsData.find(m => m.month === month && m.date === day);
  return entry ? entry.miqaats : [];
}
